import React, { createContext, useContext, useState, useCallback } from 'react';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);

  // Returns a Promise<boolean> — awaitable just like window.confirm()
  const confirm = useCallback(({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', variant = 'danger' }) => {
    return new Promise((resolve) => {
      setDialog({ title, message, confirmLabel, cancelLabel, variant, resolve });
    });
  }, []);

  const handleChoice = (result) => {
    dialog?.resolve(result);
    setDialog(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <ConfirmDialog
          {...dialog}
          onConfirm={() => handleChoice(true)}
          onCancel={() => handleChoice(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside ConfirmProvider');
  return ctx;
};

/* ── Dialog UI ─────────────────────────────────────────────── */

const ConfirmDialog = ({ title, message, confirmLabel, cancelLabel, variant, onConfirm, onCancel }) => {
  const confirmBg   = variant === 'danger'  ? '#dc3545' : '#667eea';
  const confirmHover = variant === 'danger' ? '#c82333' : '#5a67d8';

  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onCancel();
  };

  // Close on Escape
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel]);

  return (
    <div style={styles.overlay} onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div style={styles.dialog}>
        <h3 id="confirm-title" style={styles.title}>{title}</h3>
        {message && <p style={styles.message}>{message}</p>}
        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={onCancel} autoFocus>
            {cancelLabel}
          </button>
          <button
            style={{ ...styles.confirmBtn, background: confirmBg }}
            onMouseEnter={e => e.currentTarget.style.background = confirmHover}
            onMouseLeave={e => e.currentTarget.style.background = confirmBg}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9998,
    animation: 'fadeIn 0.15s ease',
  },
  dialog: {
    background: '#fff',
    borderRadius: '12px',
    padding: '28px 32px',
    maxWidth: '440px',
    width: 'calc(100vw - 40px)',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
    animation: 'slideUp 0.2s ease',
  },
  title: {
    margin: '0 0 12px',
    fontSize: '18px',
    fontWeight: 600,
    color: '#2c3e50',
  },
  message: {
    margin: '0 0 24px',
    fontSize: '14px',
    color: '#6c757d',
    lineHeight: 1.5,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
  },
  cancelBtn: {
    padding: '8px 20px',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    background: '#fff',
    color: '#2c3e50',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  confirmBtn: {
    padding: '8px 20px',
    border: 'none',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
};
